import {connect} from 'react-redux';

import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../srcCommon/helpers/reselectHelpers';
import {CalendarTypesEnum, InterfaceEventsEnum, trackInterfaceEvent} from '../../services/tracking';
import {getHasNoCalendars} from '../../store/data/calendarVersions/calendarVersionsSelectors';
import {getShouldCalendarDisplayWeekends} from '../../store/data/preferences/preferencesSelectors';
import {
  popCalendarEventAction,
  updateCalendarFocusedDateAction,
} from '../../store/panels/calendars/calendarsPanelActions';
import {
  makeGetCalendarEventData,
  makeGetCalendarsPanel,
  makeGetCalendarsParameters,
  makeGetPopoverArbitraryCalendarEventData,
  makeGetViewingNowCalendars,
} from '../../store/panels/calendars/calendarsPanelSelectors';
import {CalendarEventGridParameters} from '../../store/panels/calendars/calendarsPanelState';
import {CalendarsPanelId} from '../../store/panels/panelStateBase';
import {LargeCalendars} from './largeCalendars';

/*
 * Props.
 */

interface LargeCalendarsContainerProps {
  panelId: CalendarsPanelId;
  isVisible: boolean;
}

/*
 * Selectors.
 */

function makeGetFocusedDate(getParameters: ReturnType<typeof makeGetCalendarsParameters>) {
  return createSelector([getParameters], (parameters) => parameters.focusedDate);
}

function makeGetFocusedCalendarEventParameters(getCalendarsPanel: ReturnType<typeof makeGetCalendarsPanel>) {
  return createSelector([getCalendarsPanel], (panel) => panel.focusedCalendarEventParameters);
}

function makeGetPopoverCalendarEventParameters(getCalendarsPanel: ReturnType<typeof makeGetCalendarsPanel>) {
  return createSelector([getCalendarsPanel], (panel) => panel.popoverCalendarEventParameters);
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<LargeCalendarsContainerProps>()(() => {
  const getCalendarsPanel = makeGetCalendarsPanel();
  const getParameters = makeGetCalendarsParameters(getCalendarsPanel);
  const getFocusedDate = makeGetFocusedDate(getParameters);

  const getFocusedCalendarEventParameters = makeGetFocusedCalendarEventParameters(getCalendarsPanel);
  const getFocusedCalendarEventData = makeGetCalendarEventData(getFocusedCalendarEventParameters);

  const getPopoverCalendarEventParameters = makeGetPopoverCalendarEventParameters(getCalendarsPanel);
  const getPopoverCalendarEventData = makeGetCalendarEventData(getPopoverCalendarEventParameters);

  const getViewingNowCalendars = makeGetViewingNowCalendars(getCalendarsPanel);
  const getPopoverArbitraryCalendarEventData = makeGetPopoverArbitraryCalendarEventData(
    getPopoverCalendarEventParameters,
    getViewingNowCalendars,
  );

  return {
    hasNoCalendars: getHasNoCalendars,
    shouldCalendarDisplayWeekends: getShouldCalendarDisplayWeekends,
    focusedDate: getFocusedDate,
    focusedCalendarEventData: getFocusedCalendarEventData,
    popoverCalendarEventData: getPopoverCalendarEventData,
    popoverArbitraryCalendarEventData: getPopoverArbitraryCalendarEventData,
  };
});

const mapDispatchToProps = makeMapDispatchToProps<LargeCalendarsContainerProps>()(({panelId}) => ({
  onEventClick: (eventParameters: CalendarEventGridParameters) => {
    trackInterfaceEvent(InterfaceEventsEnum.CLICK_CALENDAR_EVENT, {
      calendar_type: CalendarTypesEnum.LARGE,
    });

    return popCalendarEventAction(panelId, eventParameters);
  },
  onPopoverRequestClose: () => popCalendarEventAction(panelId, undefined),
  onFocusedDateSelect: (focusedDate: number) => updateCalendarFocusedDateAction(panelId, focusedDate),
}));

export const LargeCalendarsContainer = connect(mapStateToProps, mapDispatchToProps)(LargeCalendars);
